import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { CalendarDays } from "lucide-react";
import {
  card,
  cardTitle,
  bodySecondary,
  dividerInner,
  emptyState,
  iconSize,
} from "../../../lib/design-system";
import { cn } from "../../../lib/utils";

const WeeklyPlanTable = ({ course }) => {
  const columns = [
    {
      accessorKey: "weekNumber",
      header: "Week",
    },
    {
      accessorKey: "topics",
      header: "Topics",
      cell: ({ row }) => (
        <ul className="list-disc pl-5 space-y-1">
          {row.original.topics.map((topic, index) => (
            <li key={index} className="text-gray-700 dark:text-gray-300 text-sm">
              {topic}
            </li>
          ))}
        </ul>
      ),
    },
  ];

  const table = useReactTable({
    data: course.weeklyPlan || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const hasData = course.weeklyPlan?.length > 0;

  return (
    <div className={cn(card, "overflow-hidden")}>
      {/* Header */}
      <div className={cn("p-6 border-b", dividerInner)}>
        <h3 className={cardTitle}>Weekly Plan</h3>
        <p className={cn(bodySecondary, "mt-1")}>
          Course schedule and topics by week
        </p>
      </div>

      <div className="p-6">
        {hasData ? (
          <div className="overflow-x-auto rounded-sm border border-gray-200 dark:border-gray-700">
            <table className="w-full border-collapse min-w-[500px]">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="bg-gray-50 dark:bg-gray-900">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={cn(
                          "border-b px-4 py-2.5 text-left text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400",
                          dividerInner
                        )}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={cn(
                      "hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors",
                      idx !== table.getRowModel().rows.length - 1 &&
                        cn("border-b", dividerInner)
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 align-top"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={emptyState}>
            <CalendarDays
              className={cn(
                iconSize.empty,
                "text-gray-400 dark:text-gray-500 mx-auto mb-3"
              )}
            />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              No Weekly Plan Available
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              The weekly plan for this course has not been set up yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyPlanTable;
